import { z } from 'zod';
const idStringSchema = z.string().trim().min(1);
const timestampSchema = z.string().trim().min(1);
const jsonObjectSchema = z.record(z.string(), z.unknown());
const nullableJsonObjectSchema = jsonObjectSchema.nullable();
export const opportunitySourceTypeSchema = z.enum([
    'polymarket_trade',
    'sports_verifier',
    'crypto_signal',
]);
export const opportunitySourceEventTypeSchema = z.enum([
    'trigger_observed',
    'trade_sampled',
    'verifier_attempt_updated',
    'signal_aggregated',
]);
export const opportunityDecisionTypeSchema = z.enum([
    'visibility_opened',
    'visibility_updated',
    'visibility_closed',
    'verifier_conclusion',
    'snapshot_refreshed',
]);
export const opportunityStateSchema = z.enum([
    'detected',
    'visible',
    'cooling_down',
    'expired',
    'closed',
]);
export const opportunityRoundStateSchema = z.enum([
    'open',
    'post_expiry_retained',
    'closed',
]);
export const opportunityCloseReasonSchema = z.enum([
    'expired',
    'superseded',
    'manual_close',
    'source_inactive',
]);
export const verifierStateForHistorySchema = z.enum([
    'pending',
    'unverified',
    'verified_no_arb',
    'verified_arb',
]);
export const opportunitySourceEventSchema = z.object({
    id: idStringSchema,
    sourceEventId: idStringSchema,
    opportunityId: idStringSchema,
    opportunityKey: idStringSchema,
    sourceType: opportunitySourceTypeSchema,
    eventType: opportunitySourceEventTypeSchema,
    eventTimestamp: timestampSchema,
    receivedAt: timestampSchema,
    triggerId: idStringSchema.nullable().default(null),
    polymarketMarketId: idStringSchema.nullable().default(null),
    polymarketOutcomeId: idStringSchema.nullable().default(null),
    polymarketClobTokenId: idStringSchema.nullable().default(null),
    representativeTrade: nullableJsonObjectSchema,
    verificationAttemptId: idStringSchema.nullable().default(null),
    verifierSnapshot: nullableJsonObjectSchema,
    sourceSnapshot: nullableJsonObjectSchema,
    createdAt: timestampSchema,
});
export const opportunityDecisionEventSchema = z.object({
    id: idStringSchema,
    decisionEventId: idStringSchema,
    opportunityId: idStringSchema,
    opportunityRoundId: idStringSchema.nullable().default(null),
    sourceEventId: idStringSchema.nullable().default(null),
    triggerId: idStringSchema.nullable().default(null),
    decisionType: opportunityDecisionTypeSchema,
    opportunityState: opportunityStateSchema,
    visible: z.boolean(),
    displayScoreBps: z.number().int().min(0).max(10000).nullable().default(null),
    decisionTimestamp: timestampSchema,
    decisionSnapshot: nullableJsonObjectSchema,
    createdAt: timestampSchema,
});
export const opportunitySnapshotSchema = z.object({
    opportunityId: idStringSchema,
    opportunityKey: idStringSchema,
    currentRoundId: idStringSchema.nullable().default(null),
    sourceType: opportunitySourceTypeSchema,
    opportunityType: idStringSchema,
    eventId: idStringSchema,
    outcomeId: idStringSchema,
    outcomeLabel: idStringSchema,
    title: idStringSchema,
    state: opportunityStateSchema,
    visible: z.boolean(),
    firstDetectedAt: timestampSchema,
    firstVisibleAt: timestampSchema.nullable().default(null),
    lastSourceEventAt: timestampSchema,
    lastDecisionAt: timestampSchema,
    expiresAt: timestampSchema.nullable().default(null),
    latestSourceEventId: idStringSchema.nullable().default(null),
    latestDecisionEventId: idStringSchema.nullable().default(null),
    latestVerificationAttemptId: idStringSchema.nullable().default(null),
    currentEdgeBps: z.number().int().min(-10000).max(10000).nullable().default(null),
    verifierState: verifierStateForHistorySchema.nullable().default(null),
    currentSnapshot: nullableJsonObjectSchema,
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
});
export const opportunityRoundSchema = z.object({
    id: idStringSchema,
    opportunityRoundId: idStringSchema,
    opportunityId: idStringSchema,
    roundSequence: z.number().int().positive(),
    state: opportunityRoundStateSchema,
    detectedAt: timestampSchema,
    openedAt: timestampSchema,
    firstVisibleAt: timestampSchema,
    closedAt: timestampSchema.nullable().default(null),
    closeReason: opportunityCloseReasonSchema.nullable().default(null),
    postExpiryRetainedUntil: timestampSchema.nullable().default(null),
    frozenAt: timestampSchema.nullable().default(null),
    openingDecisionEventId: idStringSchema.nullable().default(null),
    closingDecisionEventId: idStringSchema.nullable().default(null),
    latestVerificationAttemptId: idStringSchema.nullable().default(null),
    representativeSourceSnapshot: nullableJsonObjectSchema,
    frozenOpportunitySnapshot: nullableJsonObjectSchema,
    frozenVerifierSnapshot: nullableJsonObjectSchema,
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
});
const runtimeHealthSchema = z.enum(['healthy', 'blocked']);
const providerReachabilitySchema = z.enum(['healthy', 'blocked', 'unknown']);
export const opportunityRuntimeStatusSchema = z.object({
    verifierReady: z.boolean(),
    activePolicyPresent: z.boolean(),
    runtimeReadyMappings: z.number().int().min(0),
    watcherDelivery: runtimeHealthSchema,
    projection: runtimeHealthSchema,
    providerReachability: providerReachabilitySchema,
    feed: runtimeHealthSchema,
    recentOutputCount: z.number().int().min(0),
    blockers: z.array(idStringSchema),
});
export const opportunityOperationsSummarySchema = z.object({
    generatedAt: timestampSchema,
    windowStart: timestampSchema,
    windowMinutes: z.number().int().positive(),
    current: z.object({
        totalOpportunities: z.number().int().min(0),
        visibleOpportunities: z.number().int().min(0),
        openRounds: z.number().int().min(0),
        closedRounds: z.number().int().min(0),
        verifiedArb: z.number().int().min(0),
        unverified: z.number().int().min(0),
    }),
    reviewMetrics: z.object({
        sampleRounds: z.number().int().min(0),
        reviewedRounds: z.number().int().min(0),
        unreviewedRounds: z.number().int().min(0),
        usefulRounds: z.number().int().min(0),
        notUsefulRounds: z.number().int().min(0),
        inconclusiveRounds: z.number().int().min(0),
        invalidSignalRounds: z.number().int().min(0),
        usefulRateBps: z.number().int().min(0).max(10000),
        reviewedRateBps: z.number().int().min(0).max(10000),
        verdictLabelCounts: z.object({
            useful: z.number().int().min(0),
            not_useful: z.number().int().min(0),
            inconclusive: z.number().int().min(0),
            invalid_signal: z.number().int().min(0),
        }),
    }),
    runtimeStatus: opportunityRuntimeStatusSchema,
    recentOpportunities: z.array(opportunitySnapshotSchema),
    recentRounds: z.array(opportunityRoundSchema),
});
